import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { speakerColorVar } from '@/internationalization/speaker-color'
import { useT } from '@/internationalization/i18n'
import type { TalkTimeRow } from '@/types'

/**
 * Talk time per participant, horizontal so long names stay readable.
 *
 * Bars use the speaker palette, so a person is the same colour here as in the
 * transcript. "Unattributed" resolves to the grey unknown token for free,
 * because speakerColorVar treats it as unnamed.
 */
export function TalkTimeChart({ data }: { data: TalkTimeRow[] }) {
  const t = useT()
  return (
    <div className="w-full" style={{ height: Math.max(180, data.length * 36) }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, bottom: 0, left: 8 }}>
          <CartesianGrid stroke="hsl(var(--sb-border))" strokeDasharray="3 3" horizontal={false} />
          <XAxis type="number" stroke="hsl(var(--sb-fg-subtle))" fontSize={11} tickLine={false} axisLine={false} />
          <YAxis
            type="category"
            dataKey="participant"
            stroke="hsl(var(--sb-fg-subtle))"
            fontSize={11}
            width={110}
            tickLine={false}
            axisLine={false}
          />
          <Tooltip
            cursor={{ fill: 'hsl(var(--sb-surface-muted))' }}
            contentStyle={{
              background: 'hsl(var(--sb-surface-raised))',
              border: '1px solid hsl(var(--sb-border))',
              borderRadius: 8,
              fontSize: 12,
              color: 'hsl(var(--sb-fg))',
            }}
            formatter={(value: number) => [t('chart.minutes', { value }), t('chart.talkTime')]}
          />
          <Bar dataKey="minutes" radius={[0, 4, 4, 0]} barSize={16}>
            {data.map((row) => (
              <Cell key={row.participant} fill={`hsl(var(${speakerColorVar(row.participant)}))`} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}
